import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { Post, Get } from "../../queries/utils/api";
import { loadStripe } from "@stripe/stripe-js";
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from "@stripe/react-connect-js";
import { useStripeConnect } from "./useStripeConnect";
import GeniePage from "../../components/special/genie-page/GeniePage";
import GenieText from "../../components/core/genie-text/GenieText";
import GenieStickyHeader from "../../components/core/genie-sticky-header/GenieStickyHeader";
import { Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add } from "@mui/icons-material";

// // Initialize Stripe
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

export default function StripeConnect() {
  const { org_id } = useParams<{ org_id: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [accountCreatePending, setAccountCreatePending] = useState(false);
  const [onboardingExited, setOnboardingExited] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [connectedAccountId, setConnectedAccountId] = useState<
    string | undefined
  >();
  const [accountStatus, setAccountStatus] = useState<{
    charges_enabled: boolean;
    details_submitted: boolean;
    payouts_enabled: boolean;
    account_id: string | null;
  } | null>(null);
  const stripeConnectInstance = useStripeConnect(connectedAccountId, org_id!);
  const theme = useTheme();

  // Auto-trigger handleGetStarted when page loads and account status is checked
  useEffect(() => {
    if (!isLoading && !connectedAccountId && !accountCreatePending && !error) {
      handleGetStarted();
    }
  }, [isLoading, connectedAccountId, accountCreatePending, error]);

  // Fetch account status which includes both the account ID and status information
  useEffect(() => {
    const fetchAccountStatus = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await Get<{
          charges_enabled: boolean;
          details_submitted: boolean;
          payouts_enabled: boolean;
          account_id: string | null;
        }>(`/api/stripe/organization/${org_id}/account-status`, accessToken);
        setAccountStatus(response);
        if (response.account_id) {
          setConnectedAccountId(response.account_id);
        }
      } catch (err) {
        console.error("Failed to fetch account status:", err);
      } finally {
        console.log("ACCOUNT ID", connectedAccountId);
        console.log("ACCOUNT STATUS", accountStatus);
        setIsLoading(false);
      }
    };

    fetchAccountStatus();
  }, [org_id, getAccessTokenSilently]);

  const handleGetStarted = async () => {
    setAccountCreatePending(true);
    setError(false);
    try {
      const accessToken = await getAccessTokenSilently();
      const { account, error } = await Post<{
        account: string;
        error?: string;
      }>(
        `/api/stripe/organization/${org_id}/create-connect-account`,
        accessToken,
        {}
      );

      setAccountCreatePending(false);
      if (account) {
        setConnectedAccountId(account);
      }
      if (error) {
        setError(true);
      }
    } catch (err) {
      setError(true);
      setAccountCreatePending(false);
    }
  };

  if (isLoading) {
    return (
      <GeniePage noMargin>
        <GenieStickyHeader>
          <Stack flexDirection={"row"} width={"100%"} alignItems={"center"}>
            <GenieText fontSize={"medium"} fontWeight={"bold"}>
              Set Up Payments
            </GenieText>
          </Stack>
        </GenieStickyHeader>
        <Box mt={1} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </GeniePage>
    );
  }

  return (
    <GeniePage noMargin>
      <GenieStickyHeader>
        <Stack flexDirection={"row"} width={"100%"} alignItems={"center"}>
          <GenieText fontSize={"medium"} fontWeight={"bold"}>
            Set Up Payments
          </GenieText>
        </Stack>
      </GenieStickyHeader>
      <Box mt={1}>
        {/* Initial state - show Get Started */}
        {!connectedAccountId && !accountCreatePending && (
          <>
            <Box mt={4} mb={2}>
              <GenieText fontSize={"small"}>
                Connect your Stripe account to start accepting payments through
                our platform. This integration enables secure payment processing
                for your transactions.
              </GenieText>
            </Box>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  height: "40px",
                  textTransform: "none",
                  fontWeight: "normal",
                  fontSize: "1rem",
                  borderWidth: "2px",
                  "&:hover": {
                    borderWidth: "2px",
                    backgroundColor: "rgba(161, 154, 237, 0.04)",
                  },
                }}
                onClick={handleGetStarted}
              >
                Get Started
              </Button>
            </Box>
          </>
        )}

        {/* Error state */}
        {error && (
          <Box mt={2}>
            <Alert severity="error">
              Something went wrong while setting up your Stripe account. Please
              try again.
            </Alert>
          </Box>
        )}

        {/* Loading state */}
        {accountCreatePending && (
          <Box mt={1} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {/* Account status */}
        {connectedAccountId && accountStatus && (
          <Box sx={{ bgcolor: "background.paper", borderRadius: 1, p: 2 }}>
            {!accountStatus.details_submitted ? (
              // <Alert severity="info" sx={{ mb: 2 }}>
              //   Set up your Stripe account to receive payments and keep your bank info secure. This takes about 5 minutes - grab a coffee and have your EIN handy!
              // </Alert>
              <p>
                Set up your Stripe account to receive payments and keep your
                bank info secure. This takes about 5 minutes - grab a coffee and
                have your EIN handy!
              </p>
            ) : !accountStatus.charges_enabled ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your account is being reviewed by Stripe. You'll be able to
                accept payments once approved.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                Your account is fully set up and ready to accept payments
              </Alert>
            )}
          </Box>
        )}

        {/* Show onboarding component when account is created */}
        {stripeConnectInstance &&
          accountStatus &&
          !accountStatus.details_submitted && (
            <Box mt={2} mb={10}>
              <ConnectComponentsProvider
                connectInstance={stripeConnectInstance}
              >
                <ConnectAccountOnboarding
                  onExit={() => setOnboardingExited(true)}
                />
              </ConnectComponentsProvider>
            </Box>
          )}
      </Box>
    </GeniePage>
  );
}
